import { type UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetParking } from 'src/features/parkingManagement/api/useGetParking.ts';
import { type CreateZoneUserAccessFormSchemaValues } from 'src/features/parkingManagement/api/ZoneUserAccess/useCreateZoneUserAccess';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { membershipRoleOptions } from 'src/features/userManagement/utils';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect.tsx';
import { generalValidationMessages } from 'src/utils/messages.ts';

type ZoneUserAccessFormProps = {
    form: UseFormReturn<CreateZoneUserAccessFormSchemaValues>;
    onSubmit: (values: CreateZoneUserAccessFormSchemaValues) => void;
};

export default function ZoneUserAccessForm({ onSubmit, form }: ZoneUserAccessFormProps) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const allowedRoles: MEMBERSHIP_ROLES[] =
        useGetParking({ parkingId: parkingId || '', config: { enabled: !!parkingId } }).data?.roles || [];
    const rolesList: string[] = Object.values(MEMBERSHIP_ROLES).filter((role) => allowedRoles.includes(role));

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="zone_id"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel required variant="dialog">
                            {formatMessage(parkingManagementMessages.zoneId)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    placeholder={formatMessage(generalValidationMessages.fieldRequired)}
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e);
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="user_roles"
                render={({ field }) => {
                    const { ref: _, ...rest } = field;
                    const fieldData: string = field.value
                        ?.map((role: string): string | null => {
                            const label = membershipRoleOptions.find((option) => option.value === role)?.label;
                            return label ? formatMessage(label) : null;
                        })
                        .filter((label: string | null): label is string => typeof label === 'string')
                        .join(', ');

                    const allValues: string[] = rolesList
                        .map((role: string): string | undefined => {
                            return membershipRoleOptions.find((option): boolean => option.value === role)?.value;
                        })
                        .filter((item) => item !== undefined);

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...parkingManagementMessages.zoneUserAccessRoles} />
                                <span className="text-blue-800">*</span>
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect {...rest}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl className="w-full">
                                                {fieldData.length > 0 ? (
                                                    fieldData
                                                ) : (
                                                    <span className="text-gray-400">
                                                        <FormattedMessage
                                                            {...parkingManagementMessages.zoneUserAccessRoles}
                                                        />
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {rolesList.map((role: string) => {
                                                const label = membershipRoleOptions.find(
                                                    (recipient) => recipient.value === role,
                                                )?.label;
                                                const translatedLabel = label ? formatMessage(label) : '-';
                                                return (
                                                    <MultiSelectOption key={role} selected={role}>
                                                        {translatedLabel}
                                                    </MultiSelectOption>
                                                );
                                            })}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
        </Form>
    );
}
